"use client";

import { Translate } from "@/components/Translate";
import { createFormattedFilters } from "@/lib/depict/filters";
import type { DepictFilter } from "@/lib/depict/types";
import { clsx } from "@frend-digital/ui";
import { useUrlSearchParams } from "@frend-digital/ui/next";
import { memo, startTransition, useCallback, useMemo, useOptimistic, useRef } from "react";
import { useProducts } from "./ListingContext";
import styles from "./ProductListingPage.module.css";

const SIZE_KEY = "size_name";

/** Resolves after the browser has painted, so the click feedback shows before heavy work starts */
const afterNextPaint = () =>
	new Promise<void>((resolve) => {
		requestAnimationFrame(() => window.setTimeout(resolve, 0));
	});

const searchParamOptions = {
	replace: true,
	scroll: false,
};

const SizeButton = memo(
	({
		value,
		name,
		isSelected,
		onToggle,
	}: {
		value: string;
		name: string;
		isSelected: boolean;
		onToggle: (value: string) => void;
	}) => (
		<button
			type="button"
			className={clsx(styles.sizeItem, isSelected && styles.sizeItemSelected)}
			aria-pressed={isSelected}
			onClick={() => onToggle(value)}
		>
			<Translate>{name}</Translate>
		</button>
	),
);

SizeButton.displayName = "SizeButton";

/**
 * Quick size selection shown in the category header.
 * Shares the `size_name` search param with the filter modal.
 */
export const SizeFilter = ({ className }: { className?: string }) => {
	const { filters } = useProducts();
	const searchParams = useUrlSearchParams(searchParamOptions);
	const [selected, setOptimisticSelected] = useOptimistic(searchParams.getAll(SIZE_KEY));

	const fetchedSizes = useMemo(() => {
		const group = createFormattedFilters(filters as DepictFilter[]).find((f) => f.key === SIZE_KEY);
		return group?.values ?? [];
	}, [filters]);

	// Keep the last known sizes while a refetch is in flight so the row doesn't flicker or jump
	const lastSizes = useRef(fetchedSizes);
	if (fetchedSizes.length > 0) lastSizes.current = fetchedSizes;
	const sizes = fetchedSizes.length > 0 ? fetchedSizes : lastSizes.current;

	// Read the latest values through refs so `toggle` stays referentially stable and only
	// the buttons whose state changed re-render
	const selectedRef = useRef(selected);
	selectedRef.current = selected;
	// Bumped on every click so that, when clicking quickly, only the last one writes to the URL
	const latestClick = useRef(0);
	const searchParamsRef = useRef(searchParams);
	searchParamsRef.current = searchParams;

	const toggle = useCallback(
		(value: string) => {
			const current = selectedRef.current;
			const next = current.includes(value)
				? current.filter((v) => v !== value)
				: [...current, value];
			// Rapid clicks can happen before React re-renders, so build on this value
			selectedRef.current = next;
			const click = ++latestClick.current;

			startTransition(async () => {
				setOptimisticSelected(next);
				// Let the selected state paint before updating the URL, which re-renders the product list
				await afterNextPaint();
				if (click !== latestClick.current) return;

				const params = searchParamsRef.current;
				for (const existing of params.getAll(SIZE_KEY)) {
					params.delete(SIZE_KEY, existing);
				}
				for (const size of next) {
					params.append(SIZE_KEY, size);
				}
			});
		},
		[setOptimisticSelected],
	);

	if (sizes.length === 0) return null;

	return (
		<div className={clsx(styles.sizeFilter, className)} data-plp-size-filter>
			<h2 className={styles.sizeFilterHeading}>
				<Translate>Filter by size</Translate>
			</h2>
			<div className={styles.sizes}>
				{sizes.map((size) => (
					<SizeButton
						key={size.value}
						value={size.value}
						name={size.name}
						isSelected={selected.includes(size.value)}
						onToggle={toggle}
					/>
				))}
			</div>
		</div>
	);
};
